import React, { createContext, useContext, useEffect, useState } from 'react';
import { api } from '@/lib/api';

/**
 * PlatformSettingsProvider — fetches /api/settings/public once at boot and
 * exposes brand + content_rules to the entire app.
 *
 * Also side-effects:
 *   - Sets document.title to `${brand_name} — ${tagline}` on first load.
 *   - Injects a <link rel='icon'> favicon if the admin has uploaded one.
 *   - Writes meta description to the <head>.
 *
 * Consumers use `usePlatformSettings()` — returns
 *   { loading, brand, contentRules }.
 */
const PlatformSettingsContext = createContext({
  loading: true,
  brand: null,
  contentRules: null,
  footer: null,
  analytics: null,
});

const DEFAULT_TITLE_SUFFIX = 'IndusFact B2B Solutions';

const setFavicon = (href) => {
  if (typeof document === 'undefined' || !href) return;
  const abs = href.startsWith('http') ? href : `${process.env.REACT_APP_BACKEND_URL || ''}${href}`;
  let link = document.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = abs;
};

export const PlatformSettingsProvider = ({ children }) => {
  const [state, setState] = useState({ loading: true, brand: null, contentRules: null, footer: null, analytics: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/settings/public');
        if (cancelled) return;
        setState({
          loading: false,
          brand: data.brand || null,
          contentRules: data.content_rules || null,
          footer: data.footer || null,
          analytics: data.analytics || null,
        });

        // Side-effects on the document.
        // IMPORTANT: react-helmet-async sets <title> on route-level pages via
        // SEOHead. Our async fetch resolves AFTER Helmet has committed the
        // per-page title. Overwriting it would clobber route-level SEO.
        // Rule: only stamp the global brand fallback when NO route-level SEO
        // component has set a title yet. That's true only when the tab is
        // empty or still holds React's CRA default 'React App'.
        const brand = data.brand || {};
        // Title is owned EXCLUSIVELY by per-page <SEOHead> (react-helmet-async),
        // the single source of truth. We must NOT stamp document.title here:
        // once the static <title> was removed from index.html, stamping raced
        // with Helmet and produced a SECOND, conflicting <title> element in the
        // (prerendered) <head>. SEO pages get their title from SEOHead; private
        // app shells (dashboard/admin/auth) are noindex and need no title.
        if (brand.favicon_url) setFavicon(brand.favicon_url);
      } catch {
        // Silent fail — the app must remain functional if /public settings
        // are unreachable. Header falls back to hard-coded "IndusFact".
        if (!cancelled) {
          setState({ loading: false, brand: null, contentRules: null, footer: null, analytics: null });
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <PlatformSettingsContext.Provider value={state}>
      {children}
    </PlatformSettingsContext.Provider>
  );
};

export const usePlatformSettings = () => useContext(PlatformSettingsContext);

/**
 * Resolve a media URL. Bare paths like `/api/media/file/…` become absolute
 * against the backend URL; full URLs pass through untouched.
 */
export const resolveMediaUrl = (u) => {
  if (!u) return '';
  if (u.startsWith('http')) return u;
  return `${process.env.REACT_APP_BACKEND_URL || ''}${u}`;
};

export { DEFAULT_TITLE_SUFFIX };
